import React, { useState, useEffect } from "react";
import { fetchContentDetail, toggleContentFavorite, toggleContentReaction, IContent } from "api/student/v2/content.api";
import { getCurrentLanguageCode } from "helpers/String.helper";
import VideoPlayer from "commons/video/VideoPlayer";
import { getImageUrl } from "helpers/Image.helper";
import { formatTimeAgo } from "helpers/Datetime.helper";
import { usePopup } from "contexts/PopupContext";
import ContentCommentPopup from "./ContentCommentPopup";
import KoachInfoPopup from "./KoachInfoPopup";

interface ContentDetailPopupProps {
    contentId: number;
    onClose: () => void;
    onRefresh?: () => void;
}

const ContentDetailPopup: React.FC<ContentDetailPopupProps> = ({ contentId, onClose, onRefresh }) => {
    const { showPopup } = usePopup();
    const [content, setContent] = useState<IContent | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [favoritingContentId, setFavoritingContentId] = useState<number | null>(null);
    const [reactingContentId, setReactingContentId] = useState<number | null>(null);
    const [showReactionTooltip, setShowReactionTooltip] = useState(false);

    useEffect(() => {
        const fetchContent = async () => {
            try {
                setIsLoading(true);
                setError(null);
                const response = await fetchContentDetail(contentId);
                setContent(response.content);
            } catch (err: any) {
                console.error('콘텐츠 상세 조회 실패:', err);
                setError(err.message || '콘텐츠를 불러오는데 실패했습니다.');
            } finally {
                setIsLoading(false);
            }
        };

        if (contentId) {
            fetchContent();
        }
    }, [contentId]);

    const getTitle = (content: IContent): string => {
        if (content.title) return content.title;
        if (content.multiTexts && content.multiTexts.length > 0) {
            const currentLang = getCurrentLanguageCode();
            const currentMultiText = content.multiTexts.find(mt => mt.languageCode === currentLang);
            return currentMultiText?.title || content.multiTexts[0]?.title || '제목 없음';
        }
        return '제목 없음';
    };

    const getDesc = (content: IContent): string => {
        if (content.desc) return content.desc;
        if (content.multiTexts && content.multiTexts.length > 0) {
            const currentLang = getCurrentLanguageCode();
            const currentMultiText = content.multiTexts.find(mt => mt.languageCode === currentLang);
            return currentMultiText?.desc || content.multiTexts[0]?.desc || '';
        }
        return '';
    };

    const getKoachName = (content: IContent): string => {
        if (content.user?.userKoach?.nickname) {
            return content.user.userKoach.nickname;
        }
        if (content.user?.username) {
            return content.user.username;
        }
        return '코치';
    };

    const getKoachProfileImage = (content: IContent): string => {
        const userKoach = content.user?.userKoach;
        if (!userKoach) {
            return '/imgs/dummy/basic-img.png';
        }

        const userKoachWithImageInfo = userKoach as typeof userKoach & {
            profileImageInfo?: { id: string; url: string };
        };
        
        if (userKoachWithImageInfo.profileImageInfo?.url) {
            return userKoachWithImageInfo.profileImageInfo.url;
        }
        if (userKoach.profileImageUrl) {
            return userKoach.profileImageUrl;
        }
        if (userKoach.profileImage) {
            return getImageUrl(userKoach.profileImage, 200, 200);
        }
        return '/imgs/dummy/basic-img.png';
    };

    const handleFavoriteToggle = async (event: React.MouseEvent) => {
        if (!content || favoritingContentId === content.id) return;
        
        event.stopPropagation();
        
        try {
            setFavoritingContentId(content.id);
            const result = await toggleContentFavorite(content.id);
            
            setContent(prev => prev ? {
                ...prev,
                isFavorite: result.isFavorite,
                favoriteCount: result.favoriteCount
            } : null);
        } catch (error) {
            console.error("즐겨찾기 토글 실패:", error);
        } finally {
            setFavoritingContentId(null);
        }
    };

    const handleReactionClick = (event: React.MouseEvent) => {
        event.stopPropagation();
        setShowReactionTooltip(!showReactionTooltip);
    };

    const handleReactionSelect = async (reactionType: string, event: React.MouseEvent) => {
        if (!content || reactingContentId === content.id) return;
        
        event.stopPropagation();
        
        try {
            setReactingContentId(content.id);
            const result = await toggleContentReaction(content.id, reactionType);
            
            setContent(prev => prev ? {
                ...prev,
                myReactionTypeCode: result.isReactionActive ? result.reactionTypeCode : null,
                reactionCount: result.reactionCount
            } : null);
            setShowReactionTooltip(false);
        } catch (error) {
            console.error("리액션 토글 실패:", error);
        } finally {
            setReactingContentId(null);
        }
    };

    const handleCommentClick = (event: React.MouseEvent) => {
        if (!content) return;
        
        event.stopPropagation();
        
        const popupContent = (
            <ContentCommentPopup
                contentId={content.id}
                contentTitle={getTitle(content)}
                contentDesc={getDesc(content)}
                onClose={() => showPopup(null)}
                onRefresh={() => {
                    if (onRefresh) onRefresh();
                    // 콘텐츠 상세도 새로고침
                    const fetchContent = async () => {
                        try {
                            const response = await fetchContentDetail(contentId);
                            setContent(response.content);
                        } catch (err: any) {
                            console.error('콘텐츠 상세 조회 실패:', err);
                        }
                    };
                    fetchContent();
                }}
            />
        );
        showPopup(popupContent);
    };

    const handleKoachClick = (event: React.MouseEvent) => {
        if (!content) return;
        
        event.stopPropagation();
        
        const koachId = content.user?.userKoach?.id;
        if (!koachId) {
            console.warn('코치 정보가 없습니다.');
            return;
        }

        const popupContent = (
            <KoachInfoPopup
                koachId={koachId.toString()}
                onClose={() => showPopup(null)}
            />
        );
        showPopup(popupContent);
    };

    // 리액션 툴팁 외부 클릭 시 닫기
    useEffect(() => {
        const handleClickOutside = () => {
            if (showReactionTooltip) {
                setShowReactionTooltip(false);
            }
        };
        
        document.addEventListener('click', handleClickOutside);
        return () => document.removeEventListener('click', handleClickOutside);
    }, [showReactionTooltip]);

    // CSS 애니메이션 정의
    const fadeInUpAnimation = `
        @keyframes fadeInUp {
            from {
                opacity: 0;
                transform: translateY(20px);
            }
            to {
                opacity: 1;
                transform: translateY(0);
            }
        }
        
        @keyframes fadeIn {
            from {
                opacity: 0;
            }
            to {
                opacity: 1;
            }
        }
    `;

    if (isLoading) {
        return (
            <>
                <style>{fadeInUpAnimation}</style>
                <div 
                    className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50"
                    style={{
                        animation: 'fadeIn 0.4s ease-out forwards',
                        opacity: 0
                    }}
                    onClick={onClose}
                >
                    <div 
                        className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto"
                        style={{
                            animation: 'fadeInUp 0.4s ease-out forwards',
                            transform: 'translateY(20px)',
                            opacity: 0
                        }}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="animate-pulse p-6">
                            <div className="h-6 bg-gray-200 rounded w-3/4 mb-4"></div>
                            <div className="h-4 bg-gray-200 rounded w-full mb-2"></div>
                            <div className="h-4 bg-gray-200 rounded w-5/6 mb-4"></div>
                            <div className="h-64 bg-gray-200 rounded-lg mb-4"></div>
                        </div>
                    </div>
                </div>
            </>
        );
    }

    if (error || !content) {
        return (
            <>
                <style>{fadeInUpAnimation}</style>
                <div 
                    className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50"
                    style={{
                        animation: 'fadeIn 0.4s ease-out forwards',
                        opacity: 0
                    }}
                    onClick={onClose}
                >
                    <div 
                        className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 p-6"
                        style={{
                            animation: 'fadeInUp 0.4s ease-out forwards',
                            transform: 'translateY(20px)',
                            opacity: 0
                        }}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="text-center">
                            <div className="text-red-500 text-4xl mb-3">⚠️</div>
                            <h3 className="text-base font-semibold text-gray-900 mb-2">
                                콘텐츠 로드 실패
                            </h3>
                            <p className="text-gray-600 text-sm mb-3">
                                {error || "콘텐츠를 불러올 수 없습니다."}
                            </p>
                            <button
                                onClick={onClose}
                                className="px-3 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors text-sm"
                            >
                                닫기
                            </button>
                        </div>
                    </div>
                </div>
            </>
        );
    }

    return (
        <>
            <style>{fadeInUpAnimation}</style>
            <div 
                className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50"
                style={{
                    animation: 'fadeIn 0.4s ease-out forwards',
                    opacity: 0
                }}
                onClick={onClose}
            >
                <div 
                    className="relative bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] flex flex-col"
                    style={{
                        animation: 'fadeInUp 0.4s ease-out forwards',
                        transform: 'translateY(20px)',
                        opacity: 0
                    }}
                    onClick={(e) => e.stopPropagation()}
            >
                    {/* 스크롤 가능한 콘텐츠 영역 */}
                    <div className="flex-1 overflow-y-auto">
                        {/* 콘텐츠 카드 */}
                        <div className="relative bg-white rounded-lg overflow-hidden">
                            {/* 콘텐츠 헤더 */}
                            <div className="px-4 sm:px-6 pt-4 sm:pt-6 pb-2">
                                <div className="flex items-start justify-between mb-2">
                                    <div className="flex-1">
                                        <div className="space-y-2">
                                            {/* 첫 번째 줄: 코치 정보와 날짜 */}
                                            <div className="flex items-center justify-between">
                                                <button 
                                                    className="flex items-center gap-2 hover:opacity-80 transition-opacity cursor-pointer"
                                                    onClick={handleKoachClick}
                                                >
                                                    <img 
                                                        src={getKoachProfileImage(content)} 
                                                        alt={getKoachName(content)}
                                                        className="w-9 h-9 rounded-full object-cover"
                                                        onError={(e) => {
                                                            const target = e.target as HTMLImageElement;
                                                            target.src = '/imgs/dummy/basic-img.png';
                                                        }}
                                                    />
                                                    <span className="text-sm text-gray-600 hover:text-[#CF3688] transition-colors">{getKoachName(content)}</span>
                                                </button>
                                                <p className="text-sm text-gray-500">
                                                    {formatTimeAgo(content.createdAt)}
                                                </p>
                                            </div>

                                            {/* 두 번째 줄: 제목 */}
                                            <div className="flex items-center justify-between">
                                                <h3 className="text-lg sm:text-xl font-bold text-gray-900 break-words overflow-hidden">
                                                    {getTitle(content)}
                                                </h3>
                                            </div>
                                        </div>
                                    </div>
                                    {/* 닫기 버튼 - 헤더 영역 */}
                                    <button 
                                        onClick={onClose}
                                        className="w-8 h-8 bg-gray-100 hover:bg-gray-200 text-gray-600 rounded-full flex items-center justify-center transition-all flex-shrink-0 ml-4"
                                        aria-label="닫기"
                                    >
                                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                                        </svg>
                                    </button>
                                </div>
                            </div>

                        {/* 콘텐츠 내용 */}
                        <div className="px-4 sm:px-6 pt-2 pb-4 sm:pb-6">
                            {/* 설명 */}
                            {getDesc(content) && (
                                <div className="mb-4">
                                    <p className="text-gray-700 text-sm sm:text-base leading-relaxed whitespace-pre-wrap break-words">
                                        {getDesc(content)}
                                    </p>
                                </div>
                            )}

                            {/* 비디오 */}
                            {content.video && (
                                <div className="mb-4">
                                    <VideoPlayer
                                        video={content.video}
                                        className="w-full rounded-lg border border-gray-200"
                                        enableVideoLog={false}
                                    />
                                </div>
                            )}

                            {/* 하단 액션 바: Reaction, 댓글, 즐겨찾기 */}
                            <div className="flex items-center justify-between pt-4 border-t border-gray-200">
                                {/* 왼쪽: Reaction과 댓글 */}
                                <div className="flex items-center space-x-4">
                                    {/* Reaction */}
                                    <div className="relative">
                                        <button
                                            onClick={handleReactionClick}
                                            className={`flex items-center space-x-1 transition-colors cursor-pointer ${
                                                content.myReactionTypeCode 
                                                    ? 'text-[#CF3688]' 
                                                    : 'text-gray-500 hover:text-[#CF3688]'
                                            }`}
                                        >
                                            {content.myReactionTypeCode ? (
                                                <img 
                                                    src={
                                                        content.myReactionTypeCode === 'reactionType:veryHelpful'
                                                            ? '/imgs/icons/icon-reaction-very-helpful-active.svg'
                                                            : content.myReactionTypeCode === 'reactionType:helpful'
                                                            ? '/imgs/icons/icon-reaction-helpful-active.svg'
                                                            : content.myReactionTypeCode === 'reactionType:disappointed'
                                                            ? '/imgs/icons/icon-reaction-disappointed-active.svg'
                                                            : '/imgs/icons/icon-reaction-very-helpful.svg'
                                                    }
                                                    alt="리액션"
                                                    className="w-5 h-5"
                                                />
                                            ) : (
                                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M14.828 14.828a4 4 0 01-5.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                                </svg>
                                            )}
                                            <span className="text-sm font-medium">
                                                {content.reactionCount || 0}
                                            </span>
                                        </button>
                                        
                                        {/* 리액션 선택 툴팁 */}
                                        {showReactionTooltip && (
                                            <div 
                                                className="absolute bottom-full left-0 mb-2 flex items-center gap-1 bg-white border border-gray-200 rounded-lg shadow-lg p-1 z-10"
                                                onClick={(e) => e.stopPropagation()}
                                            >
                                                <button
                                                    onClick={(e) => handleReactionSelect('reactionType:veryHelpful', e)}
                                                    className={`flex items-center justify-center w-10 h-10 rounded transition-colors ${
                                                        content.myReactionTypeCode === 'reactionType:veryHelpful'
                                                            ? 'bg-[#CF3688]/10'
                                                            : 'hover:bg-gray-100'
                                                    }`}
                                                    title="큰 도움이 됐어요"
                                                >
                                                    <img 
                                                        src={content.myReactionTypeCode === 'reactionType:veryHelpful' 
                                                            ? '/imgs/icons/icon-reaction-very-helpful-active.svg'
                                                            : '/imgs/icons/icon-reaction-very-helpful.svg'
                                                        }
                                                        alt="큰 도움이 됐어요"
                                                        className="w-6 h-6"
                                                    />
                                                </button>
                                                <button
                                                    onClick={(e) => handleReactionSelect('reactionType:helpful', e)}
                                                    className={`flex items-center justify-center w-10 h-10 rounded transition-colors ${
                                                        content.myReactionTypeCode === 'reactionType:helpful'
                                                            ? 'bg-[#CF3688]/10'
                                                            : 'hover:bg-gray-100'
                                                    }`}
                                                    title="도움이 됐어요"
                                                >
                                                    <img 
                                                        src={content.myReactionTypeCode === 'reactionType:helpful' 
                                                            ? '/imgs/icons/icon-reaction-helpful-active.svg'
                                                            : '/imgs/icons/icon-reaction-helpful.svg'
                                                        }
                                                        alt="도움이 됐어요"
                                                        className="w-6 h-6"
                                                    />
                                                </button>
                                                <button
                                                    onClick={(e) => handleReactionSelect('reactionType:disappointed', e)}
                                                    className={`flex items-center justify-center w-10 h-10 rounded transition-colors ${
                                                        content.myReactionTypeCode === 'reactionType:disappointed'
                                                            ? 'bg-[#CF3688]/10'
                                                            : 'hover:bg-gray-100'
                                                    }`}
                                                    title="아쉬웠어요"
                                                >
                                                    <img 
                                                        src={content.myReactionTypeCode === 'reactionType:disappointed' 
                                                            ? '/imgs/icons/icon-reaction-disappointed-active.svg'
                                                            : '/imgs/icons/icon-reaction-disappointed.svg'
                                                        }
                                                        alt="아쉬웠어요"
                                                        className="w-6 h-6"
                                                    />
                                                </button>
                                            </div>
                                        )}
                                    </div>

                                    {/* 댓글 */}
                                    <button
                                        onClick={handleCommentClick}
                                        className="flex items-center space-x-1 text-gray-500 hover:text-[#CF3688] transition-colors cursor-pointer"
                                    >
                                        <img 
                                            src="/imgs/icons/icon-comment.svg" 
                                            alt="댓글" 
                                            className="w-5 h-5"
                                        />
                                        <span className="text-sm font-medium">
                                            {content.commentCount || 0}
                                        </span>
                                    </button>
                                </div>

                                {/* 오른쪽: 즐겨찾기 */}
                                <button
                                    onClick={handleFavoriteToggle}
                                    disabled={favoritingContentId === content.id}
                                    className={`flex items-center space-x-1 transition-all duration-200 ${
                                        favoritingContentId === content.id 
                                            ? 'opacity-50 cursor-not-allowed' 
                                            : content.isFavorite 
                                                ? 'text-[#CF3688]' 
                                                : 'text-gray-500'
                                    }`}
                                >
                                    {favoritingContentId === content.id ? (
                                        <svg className="w-5 h-5 text-gray-400 animate-spin" fill="none" viewBox="0 0 24 24">
                                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                        </svg>
                                    ) : (
                                        <img
                                            src={content.isFavorite ? "/imgs/icons/icon-favorite-active.svg" : "/imgs/icons/icon-favorite.svg"}
                                            alt="즐겨찾기"
                                            className="w-5 h-5"
                                        />
                                    )}
                                    <span className="text-sm font-medium">
                                        {content.favoriteCount || 0}
                                    </span>
                                </button>
                            </div>
                        </div>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default ContentDetailPopup;
